import { useCallback, useEffect, useState } from 'react'
import { useSearchParams, useRouter, usePathname } from 'next/navigation'
import { SortingState, ColumnSort, OnChangeFn } from '@tanstack/react-table'

interface SortParams {
  legacyMode?: boolean // If true, uses separate sort & sortDirection params
  sortParam?: string
  directionParam?: string
}

function parseSortParams(
  searchParams: URLSearchParams,
  {
    legacyMode = false,
    sortParam = 'sort',
    directionParam = 'sortDirection',
  }: SortParams
): SortingState {
  if (legacyMode) {
    const sort = searchParams.get(sortParam)
    const direction = searchParams.get(directionParam)
    if (!sort) return []
    return [{ id: sort, desc: direction === 'desc' }]
  }

  const sortString = searchParams.get(sortParam)
  if (!sortString) return []
  const desc = sortString.startsWith('-')
  const id = desc ? sortString.slice(1) : sortString
  return [{ id, desc }]
}

function updateSortParams(
  params: URLSearchParams,
  sorting: SortingState,
  {
    legacyMode = false,
    sortParam = 'sort',
    directionParam = 'sortDirection',
  }: SortParams
): URLSearchParams {
  const newParams = new URLSearchParams(params)

  if (!sorting.length) {
    newParams.delete(sortParam)
    if (legacyMode) newParams.delete(directionParam)
    return newParams
  }

  const { id, desc } = sorting[0]

  if (legacyMode) {
    newParams.set(sortParam, id)
    newParams.set(directionParam, desc ? 'desc' : 'asc')
  } else {
    newParams.set(sortParam, desc ? `-${id}` : id)
  }

  return newParams
}

interface UseTableSortOptions extends SortParams {
  defaultSort?: ColumnSort
  shouldUseParams?: boolean
}

export function useTableSort({
  defaultSort,
  shouldUseParams = true,
  legacyMode = false,
  sortParam = 'sort',
  directionParam = 'sortDirection',
}: UseTableSortOptions = {}): {
  sorting: SortingState
  onSortingChange: OnChangeFn<SortingState>
  sortDirection: 'asc' | 'desc'
} {
  const pathName = usePathname()
  const router = useRouter()
  const searchParams = useSearchParams()

  const [sorting, setSorting] = useState<SortingState>(() => {
    if (shouldUseParams) {
      const urlSort = parseSortParams(searchParams, {
        legacyMode,
        sortParam,
        directionParam,
      })
      return urlSort.length ? urlSort : defaultSort ? [defaultSort] : []
    }

    return defaultSort ? [defaultSort] : []
  })

  useEffect(() => {
    const newParams = updateSortParams(searchParams, sorting, {
      legacyMode,
      sortParam,
      directionParam,
    })
    router.replace(`${pathName}?${newParams}`, {
      //since we are sorting inside of a table, we don't want the default push behavior of scrolling to the top of the page
      scroll: false,
    })
  }, [
    directionParam,
    legacyMode,
    pathName,
    router,
    searchParams,
    sortParam,
    sorting,
  ])

  const onSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      const newSorting =
        updater instanceof Function ? updater(sorting) : updater

      if (shouldUseParams) {
        const newParams = updateSortParams(searchParams, newSorting, {
          legacyMode,
          sortParam,
          directionParam,
        })
        router.replace(`${pathName}?${newParams}`)
      }

      setSorting(newSorting)
    },
    [
      pathName,
      router,
      searchParams,
      shouldUseParams,
      legacyMode,
      sortParam,
      directionParam,
      sorting,
    ]
  )

  const sortDirection = sorting.length
    ? sorting[0].desc
      ? 'desc'
      : 'asc'
    : 'asc'

  return { sorting, onSortingChange, sortDirection }
}
